import { useState } from 'react';

import type { ChangeEventHandler, KeyboardEvent, KeyboardEventHandler } from 'react';

type DecimalPrecision = {
  decimalDigits: number;
  integerDigits: number;
};

type UseValidateNumberInputParams = {
  decimalPrecision: DecimalPrecision;
};

type UseValidateNumberInputResult = {
  handleInputChange: ChangeEventHandler<HTMLInputElement>;
  handleInputKeyDown: KeyboardEventHandler<HTMLInputElement>;
  valueInput: string;
};

const DEFAULT_PRECISION: DecimalPrecision = {
  decimalDigits: 2,
  integerDigits: 10,
};

const DECIMAL_SEPARATOR = '.';

const useValidateNumberInput = (
  defaultValue?: number | string,
  customParams?: Partial<UseValidateNumberInputParams>,
): UseValidateNumberInputResult => {
  const [valueInput, setValueInput] = useState<string>(String(defaultValue) || '');

  const params: UseValidateNumberInputParams = {
    decimalPrecision: customParams?.decimalPrecision || DEFAULT_PRECISION,
  };

  const formatValue = (value: string) => {
    let formatted = value.replace(/[^\d-+,.]/, '');

    if (formatted.includes('-')) {
      formatted = formatted.replace(/-/g, '');
      formatted = formatted.startsWith('-') ? formatted : `-${formatted}`;
    }

    if (formatted.includes('+')) {
      formatted = formatted.replace(/-/g, '');
      formatted = formatted.replace(/\+/g, '');
    }

    if (formatted.includes('.') || formatted.includes(',')) {
      formatted = formatted.replace('.', DECIMAL_SEPARATOR);
      formatted = formatted.replace(',', DECIMAL_SEPARATOR);

      const [intPart, ...rest] = formatted.split(DECIMAL_SEPARATOR);
      const decPart = rest.join('');

      const limitedDecPart = decPart.slice(0, params.decimalPrecision.decimalDigits);
      const formattedIntPart = !intPart || intPart === '-' ? `0${intPart}` : intPart;

      formatted = `${formattedIntPart}${DECIMAL_SEPARATOR}${limitedDecPart}`;
    } else {
      const isNegative = formatted.startsWith('-');
      const digits = isNegative ? formatted.slice(1) : formatted;
      if (digits.length > params.decimalPrecision.integerDigits) {
        formatted =
          isNegative ?
            `-${digits.slice(0, params.decimalPrecision.integerDigits)}`
          : digits.slice(0, params.decimalPrecision.integerDigits);
      }
    }

    return formatted;
  };

  const handleInputChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const newValue = formatValue(e.target.value);
    setValueInput(newValue);
  };

  const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'Tab',
      'Home',
      'End',
      '-',
      '+',
      '.',
      ',',
      '0',
      '1',
      '2',
      '3',
      '4',
      '5',
      '6',
      '7',
      '8',
      '9',
    ];

    if (!allowedKeys.includes(e.key) || (valueInput.includes(DECIMAL_SEPARATOR) && (e.key === '.' || e.key === ','))) {
      e.preventDefault();
    }
  };

  return {
    handleInputChange,
    handleInputKeyDown,
    valueInput,
  };
};

export { useValidateNumberInput };
